import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, UserCheck, GraduationCap, Clock, Globe, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

export const HRPage = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const placeholderKpis = [
    { label: { en: 'Headcount', pt: 'Efectivos' }, icon: Users },
    { label: { en: 'Retention', pt: 'Retenção' }, icon: UserCheck },
    { label: { en: 'Training', pt: 'Formação' }, icon: GraduationCap },
    { label: { en: 'Absenteeism', pt: 'Absenteísmo' }, icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="hr-page">
      {/* Header - Premium Style */}
      <div className="bg-white border-b border-slate-200 px-8 py-3 sticky top-0 z-40 shadow-sm">
        <div className="flex items-center justify-between">
          {/* Left side: Navigation + Department + Section */}
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-primary"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Cockpit Executivo' : 'Executive Cockpit'}
            </Button>
            <div className="h-4 w-px bg-slate-300"></div>
            <span className="font-serif font-bold text-primary text-lg">
              {language === 'pt' ? 'Recursos Humanos' : 'Human Resources'}
            </span>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="font-serif text-slate-600 text-base">
              {language === 'pt' ? 'Visão Geral' : 'Overview'}
            </span>
          </div>

          {/* Right side: Company name + Language Toggle */}
          <div className="flex items-center gap-4">
            <span className="font-serif text-slate-500 text-sm hidden lg:inline">
              {language === 'pt' ? 'Empresa ABC' : 'Company ABC'}
            </span>
            <div className="h-4 w-px bg-slate-200 hidden lg:block"></div>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded hover:bg-primary/20 transition-colors text-primary"
              data-testid="hr-language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto px-8 py-16">
        <div className="flex flex-col items-center justify-center min-h-[500px]">
          {/* Visual Element */}
          <div className="relative mb-10">
            <div className="w-32 h-32 bg-slate-100 rounded-full flex items-center justify-center">
              <Users className="w-16 h-16 text-slate-300" />
            </div>
            <div className="absolute -inset-4 border-2 border-dashed border-slate-200 rounded-full animate-pulse"></div>
          </div>

          {/* Main Message */}
          <h2 className="font-serif text-3xl font-semibold text-primary mb-4 text-center">
            {t('moduleInDevelopment')}
          </h2>
          <p className="text-slate-500 max-w-xl text-center leading-relaxed mb-10">
            {t('placeholderText')}
          </p>

          {/* Ghosted KPI Cards */}
          <div className="grid grid-cols-4 gap-4 w-full max-w-3xl mb-10">
            {placeholderKpis.map((kpi, idx) => {
              const Icon = kpi.icon;
              return (
                <div 
                  key={idx} 
                  className="bg-white/60 border border-dashed border-slate-200 rounded-lg p-6 text-center opacity-50"
                >
                  <div className="w-12 h-12 bg-slate-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-slate-300" />
                  </div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">
                    {typeof kpi.label === 'object' ? kpi.label[language] : kpi.label}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Footer Note */}
          <p className="text-xs text-slate-400 uppercase tracking-wider">
            {t('activationNote')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HRPage;
